import { useState } from "react";
import { toast } from "sonner";
import { Check, Shuffle, Clock, X, ThumbsDown, Undo2, Info } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const WHY = [["proud", "Felt proud"], ["progress", "Saw progress"], ["calm", "Felt calmer"], ["fun", "It was fun"], ["energy", "More energy"], ["me_time", "Time for me"]];
const ACTIONS = [["swap", "Swap", Shuffle], ["not_today", "Not today", Clock], ["not_for_me", "Not for me", X], ["didnt_help", "Didn't help", ThumbsDown]];

export function memoryToast(memory) {
  if (!memory) return;
  toast(`Noted: ${memory.text}`, {
    description: "CAMRYN will remember this.",
    action: { label: "Undo", onClick: () => api.delete(`/v2/memories/${memory.id}`).then(() => toast.success("Forgotten")) },
  });
}

export function StepCard({ step, slot, accent, label, onDay, compact }) {
  const [busy, setBusy] = useState(false);
  const [showWhy, setShowWhy] = useState(false);
  const [asked, setAsked] = useState(false);
  const done = step.status === "done";
  const isPlan = !!step.plan_id;
  const actions = isPlan ? ACTIONS.filter(([a]) => a === "not_today") : ACTIONS;

  const act = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post("/v2/today/feedback", { slot, action });
      onDay(data.day);
      memoryToast(data.memory);
      if (isPlan && action === "not_today") toast("Back on My plans. CAMRYN's suggestion is your step again.");
      else if (isPlan && action === "done") toast.success("Done, and ticked off My plans.");
      else if (action === "not_today") toast("No problem. Here's something else for today.");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const why = async (reason) => {
    setAsked(true);
    const { data } = await api.post("/v2/today/why", { reason });
    memoryToast(data.memory);
  };

  return (
    <div data-testid={`step-card-${slot}`} className={`card ${compact ? "p-5" : "p-6"} relative overflow-hidden`}>
      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: accent }} />
      <div className="flex items-center justify-between mb-2">
        <span className="eyebrow" style={{ color: accent }}>{label}</span>
        {step.minutes != null && <span className="text-xs text-stone-500">{step.minutes} min</span>}
      </div>
      <p className={`font-display ${compact ? "text-lg" : "text-2xl"} leading-snug text-stone-900 ${done ? "line-through decoration-stone-300" : ""}`} data-testid={`step-text-${slot}`}>{step.text}</p>
      <button onClick={() => setShowWhy(!showWhy)} data-testid={`step-why-${slot}`} className="tap mt-2 text-xs text-stone-500 inline-flex items-center gap-1 hover:text-stone-800"><Info className="h-3.5 w-3.5" />Why this?</button>
      {showWhy && <p className="text-sm text-stone-600 mt-1.5" data-testid={`step-why-text-${slot}`}>{step.why}</p>}

      {done ? (
        <div className="mt-5">
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#6E8B74]"><Check className="h-4 w-4" />Done. That counts.</span>
            <button onClick={() => act("undo")} data-testid={`step-undo-${slot}`} className="text-xs text-stone-500 inline-flex items-center gap-1"><Undo2 className="h-3.5 w-3.5" />Undo</button>
          </div>
          {!asked && !isPlan && (
            <div className="mt-4" data-testid={`step-why-ask-${slot}`}>
              <p className="text-xs text-stone-500 mb-2">What did it give you? (helps CAMRYN learn what moves you)</p>
              <div className="flex flex-wrap gap-1.5">{WHY.map(([k, l]) => <button key={k} onClick={() => why(k)} data-testid={`step-why-${slot}-${k}`} className="chip text-xs px-3 py-1.5 rounded-full border border-stone-200 hover:border-stone-500">{l}</button>)}</div>
            </div>
          )}
        </div>
      ) : step.status === "didnt_help" ? (
        <p className="mt-4 text-sm text-stone-500">Thanks for telling me. I won't suggest this again.</p>
      ) : (
        <div className="mt-5 space-y-3">
          <button disabled={busy} onClick={() => act("done")} data-testid={`step-done-${slot}`} className="btn-ink w-full h-12 rounded-full font-semibold disabled:opacity-50">I did it</button>
          <div className={`grid ${isPlan ? "grid-cols-1" : "grid-cols-4"} gap-1.5`}>
            {actions.map(([a, l, Icon]) => (
              <button key={a} disabled={busy} onClick={() => act(a)} data-testid={`step-${a}-${slot}`} className="chip flex flex-col items-center gap-1 py-2 rounded-xl text-[11px] text-stone-600 hover:bg-[#F5F2EC] disabled:opacity-50">
                <Icon className="h-4 w-4" strokeWidth={1.75} />{l}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
